import { create } from 'zustand';
import geolocationService, { GeoPosition } from '../pages/delivery/services/GeolocationService';
import { isAppVersionGreaterThanOrEqual, kIOSNativeLocationVersion } from '../utils/versionUtils';

interface LocationState {
  // 当前位置信息
  position: GeoPosition | null;
  // 最后更新时间
  lastUpdatedAt: number | null;
  // 是否正在获取位置
  isLoading: boolean;
  // 位置获取错误信息
  error: Error | null;
  // 开始自动更新位置
  startAutoUpdate: () => void;
  // 停止自动更新位置
  stopAutoUpdate: () => void;
  // 内部状态更新方法
  setPosition: (position: GeoPosition | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: Error | null) => void;
}

// 自动更新间隔（毫秒）
const AUTO_UPDATE_INTERVAL = 10000; // 10秒

// 重试间隔（毫秒）
const RETRY_INTERVAL = 30000; // 30秒

// 自动更新定时器ID
let autoUpdateTimerId: number | null = null;

// 重试定时器ID
let retryTimerId: number | null = null;

// 标记是否应该尝试自动更新（用于网络恢复时的重试）
let shouldAutoUpdate = false;

// 内部私有方法，用于更新位置
const updatePosition = async (set: any, get: any): Promise<GeoPosition | null> => {
  const { isLoading } = get();
  
  // 如果正在获取位置，则直接返回当前位置
  if (isLoading) {
    return get().position;
  }
  
  set({ isLoading: true, error: null });
  
  try {
    const position = await geolocationService.getOneTimePosition();
    set({ 
      position, 
      lastUpdatedAt: Date.now(),
      isLoading: false 
    });
    return position;
  } catch (error) {
    console.log('Location update error:', error);
    set({ 
      error: error instanceof Error ? error : new Error(String(error)),
      isLoading: false 
    });
    return get().position;
  }
};

// 设置网络状态监听
const setupNetworkListeners = () => {
  if (typeof window !== 'undefined') {
    // 网络恢复时的处理
    window.addEventListener('online', async () => {
      console.log('Network is back online, checking if location updates should be restarted');
      if (shouldAutoUpdate && autoUpdateTimerId === null) {
        // 检查是否为iOS 3.0.0及以上版本
        const isIOSHigherVersion = await isAppVersionGreaterThanOrEqual(kIOSNativeLocationVersion);
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        // 如果是iOS 3.0.0及以上版本，不自动重启位置更新（之前原生授权没开，自动获取也拿不到）
        if (isIOS && isIOSHigherVersion) {
          return;
        }
        
        console.log('Restarting location updates after network recovery');
        try {
          useLocationStore.getState().startAutoUpdate();
        } catch (error) {
          console.log('Failed to restart location updates after network recovery:', error);
        }
      }
    });
    
    // 网络断开时的处理
    window.addEventListener('offline', () => {
      console.log('Network is offline, location updates may fail but will continue to try');
    });
  }
};

export const useLocationStore = create<LocationState>((set, get) => ({
  position: null,
  lastUpdatedAt: null,
  isLoading: false,
  error: null,

  // 开始自动更新位置
  startAutoUpdate: () => {
    try {
      // 标记应该自动更新
      shouldAutoUpdate = true;
      
      // 如果已经有定时器在运行，先停止它
      if (autoUpdateTimerId !== null) {
        get().stopAutoUpdate();
      }
      
      // 清除可能存在的重试定时器
      if (retryTimerId !== null) {
        clearTimeout(retryTimerId);
        retryTimerId = null;
      }
      
      // 立即获取一次位置
      updatePosition(set, get).catch(error => {
        console.log('Initial position update error in startAutoUpdate:', error);
      });
      
      // 设置定时器，每隔一段时间自动更新位置
      autoUpdateTimerId = window.setInterval(() => {
        try {
          updatePosition(set, get).catch(error => {
            console.log('Periodic position update error in interval:', error);
          });
        } catch (error) {
          console.log('Unexpected error in position update interval:', error);
          // 即使发生错误，也保持定时器继续运行
        }
      }, AUTO_UPDATE_INTERVAL);
    } catch (error) {
      console.log('Error starting location auto update:', error);
      // 如果启动自动更新失败，尝试清理可能存在的定时器
      if (autoUpdateTimerId !== null) {
        clearInterval(autoUpdateTimerId);
        autoUpdateTimerId = null;
      }
      
      // 设置重试定时器，在一段时间后尝试重新启动自动更新
      if (shouldAutoUpdate) {
        console.log(`Will retry starting location updates in ${RETRY_INTERVAL/1000} seconds`);
        retryTimerId = window.setTimeout(() => {
          if (shouldAutoUpdate) {
            console.log('Retrying to start location updates');
            try {
              get().startAutoUpdate();
            } catch (error) {
              console.log('Retry to start location updates failed:', error);
            }
          }
        }, RETRY_INTERVAL);
      }
    }
  },

  // 停止自动更新位置
  stopAutoUpdate: () => {
    try {
      // 标记不应该自动更新
      shouldAutoUpdate = false;
      
      // 清除自动更新定时器
      if (autoUpdateTimerId !== null) {
        clearInterval(autoUpdateTimerId);
        autoUpdateTimerId = null;
      }
      
      // 清除重试定时器
      if (retryTimerId !== null) {
        clearTimeout(retryTimerId);
        retryTimerId = null;
      }
    } catch (error) {
      console.log('Error stopping location auto update:', error);
      // 确保即使出错，定时器ID也被重置
      autoUpdateTimerId = null;
      retryTimerId = null;
    }
  },

  // 内部状态更新方法
  setPosition: (position) => set({ position, lastUpdatedAt: Date.now() }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}));

// 设置网络状态监听
setupNetworkListeners();

// 应用启动时自动开始位置更新
if (typeof window !== 'undefined') {
  // 确保在浏览器环境中执行
  setTimeout(async () => {
    try {
      // 检查是否为iOS 4.0.0及以上版本
      const isIOSHigherVersion = await isAppVersionGreaterThanOrEqual(kIOSNativeLocationVersion);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // 如果是iOS 4.0.0及以上版本，不自动重启位置更新（之前原生授权没开，自动获取也拿不到）
      if (isIOS && isIOSHigherVersion) {
        return;
      }
      
      useLocationStore.getState().startAutoUpdate();
    } catch (error) {
      console.log('Error auto-starting location updates:', error);
    }
  }, 1000);
}

// 当应用关闭或页面卸载时，停止位置更新
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    try {
      useLocationStore.getState().stopAutoUpdate();
    } catch (error) {
      console.log('Error stopping location updates on unload:', error);
    }
  });
} 